import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Search, Check, Loader2 } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';

interface AssignPartnerSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vehicleId: number | null;
    vehicleLabel: string;
    currentPartnerId: number | null;
}

interface PartnerOption {
    id: number;
    name: string;
}

export function AssignPartnerSheet({
    open,
    onOpenChange,
    vehicleId,
    vehicleLabel,
    currentPartnerId,
}: AssignPartnerSheetProps) {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
    const debouncedSearch = useDebounce(searchTerm, 300);

    // Pre-select current partner when sheet opens
    useEffect(() => {
        if (open) {
            setSelectedPartnerId(currentPartnerId);
            setSearchTerm('');
        }
    }, [open, currentPartnerId]);

    // Fetch all partners
    const { data: partners, isLoading } = useQuery({
        queryKey: ['partners-for-assignment', debouncedSearch],
        queryFn: async () => {
            let query = supabase
                .from('partners')
                .select('id, name')
                .order('name', { ascending: true });

            if (debouncedSearch) {
                query = query.ilike('name', `%${debouncedSearch}%`);
            }

            const { data, error } = await query;
            if (error) throw error;
            return (data || []) as PartnerOption[];
        },
        enabled: open,
    });

    // Assignment mutation
    const assignMutation = useMutation({
        mutationFn: async () => {
            if (!vehicleId || !selectedPartnerId) throw new Error('Missing vehicle or partner');
            const { error } = await supabase
                .from('vehicles')
                .update({ partner_id: selectedPartnerId })
                .eq('id', vehicleId);
            if (error) throw error;
        },
        onSuccess: () => {
            const partnerName = partners?.find(p => p.id === selectedPartnerId)?.name;
            toast.success(`Vehicle assigned to ${partnerName}`);
            invalidateRelatedQueries();
            onOpenChange(false);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const invalidateRelatedQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
        // Shared by both the List view (VehiclesRecords) and the Containers view (ProcurementContainers)
        // so an assign/reassign from either stays consistent when the user switches views.
        queryClient.invalidateQueries({ queryKey: ['procurement-containers-vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['partner-vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['partner-vehicles-page'] });
        queryClient.invalidateQueries({ queryKey: ['partner-vehicle-stats'] });
        queryClient.invalidateQueries({ queryKey: ['partner-financial-summary'] });
        queryClient.invalidateQueries({ queryKey: ['partner-recent-activity'] });
        queryClient.invalidateQueries({ queryKey: ['partners'] });
        queryClient.invalidateQueries({ queryKey: ['unassigned-vehicles-for-partner'] });
    };

    const handleAssign = () => {
        if (!selectedPartnerId || selectedPartnerId === currentPartnerId) return;
        assignMutation.mutate();
    };

    const isReassign = currentPartnerId !== null;
    const hasChanged = selectedPartnerId !== null && selectedPartnerId !== currentPartnerId;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                hideOverlay
                className="w-[500px] m-4 h-[calc(100vh-32px)] rounded-xl border flex flex-col"
            >
                <SheetHeader>
                    <SheetTitle>
                        {isReassign ? 'Reassign Partner' : 'Assign Partner'}
                    </SheetTitle>
                </SheetHeader>

                <div className="mt-4 flex flex-col flex-1 min-h-0">
                    {/* Vehicle Summary */}
                    <div className="p-4 bg-muted/50 rounded-lg mb-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Vehicle</span>
                            <span className="text-sm font-medium">{vehicleLabel}</span>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search partners..."
                            className="pl-9 focus-visible:ring-ring"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Partner List */}
                    <ScrollArea className="flex-1">
                        <div className="space-y-2 pr-4">
                            {isLoading && (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            )}

                            {!isLoading && partners?.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                    <p className="text-sm">No partners found</p>
                                    {searchTerm && (
                                        <p className="text-xs mt-1">Try a different search term</p>
                                    )}
                                </div>
                            )}

                            {partners?.map((partner) => {
                                const isCurrent = partner.id === currentPartnerId;
                                const isSelected = partner.id === selectedPartnerId;

                                return (
                                    <button
                                        key={partner.id}
                                        onClick={() => setSelectedPartnerId(partner.id)}
                                        className={`w-full text-left p-3 rounded-lg border transition-all hover:bg-muted/50 ${
                                            isSelected
                                                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                                : isCurrent
                                                    ? 'border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-900/20'
                                                    : 'border-border'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="font-medium">{partner.name}</span>
                                            <div className="flex items-center gap-2">
                                                {isCurrent && (
                                                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                                        Current
                                                    </Badge>
                                                )}
                                                {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </ScrollArea>

                    {/* Actions */}
                    <div className="pt-4 border-t mt-4">
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex-1"
                                onClick={handleAssign}
                                disabled={!hasChanged || assignMutation.isPending}
                            >
                                {assignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                {isReassign ? 'Reassign' : 'Assign'}
                            </Button>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
